import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAssetById } from '@/hooks/useAssetById';
import { EmbeddedAssetViewer } from '@/components/assets/EmbeddedAssetViewer';
import { SideBySideEditor } from '@/components/assets/SideBySideEditor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit3, ChevronRight, X, Save, Image } from 'lucide-react';
import { toast } from 'sonner';
import { updateAsset } from '@/utils/graphqlClient';

const AssetDetailPage = () => {
  const { collectionId, assetId } = useParams<{ collectionId: string; assetId: string }>();
  const { asset, loading, error, refetch } = useAssetById(assetId!);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedHeader, setEditedHeader] = useState('');
  const [editedSubheader, setEditedSubheader] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editorUrls, setEditorUrls] = useState<{ url1: string; url2: string } | null>(null);

  const handleEdit = async () => {
    if (!assetId || !asset) {
      toast.error('Asset not found');
      return;
    }

    try {
      const comparisonMode = asset.comparison_mode || 'side_by_side';

      // Helper function to get URL with fallbacks
      const getUrl = (prefix: 'primary' | 'secondary') => {
        return asset[`${prefix}_cropped_url`] ||
               asset[`${prefix}_original_url`] ||
               asset[`${prefix}_image`];
      };

      if (comparisonMode === 'single') {
        // Single mode: edit whichever image exists (prefer secondary, fallback to primary)
        const url2 = getUrl('secondary') || getUrl('primary');

        if (!url2) {
          toast.error('No image available for editing');
          return;
        }

        // Open editor with only the image (pass same URL for both to maintain compatibility)
        setEditorUrls({ url1: url2, url2 });
        setIsEditMode(true);
      } else {
        // Side-by-side mode: edit both images
        let url1 = getUrl('primary');
        let url2 = getUrl('secondary');

        if (!url1 || !url2) {
          toast.error('Both images are required for side-by-side editing');
          return;
        }

        // Open editor with both images
        setEditorUrls({ url1, url2 });
        setIsEditMode(true);
      }
    } catch (error) {
      console.error('Error opening editor:', error);
      toast.error('Failed to open editor');
    }
  };

  const handleSaveImages = async (url1: string, url2: string) => {
    if (!assetId || !asset) {
      toast.error('No asset selected for editing');
      return;
    }

    try {
      // In single mode, url1 and url2 are the same
      // Update both to ensure consistency
      const updateData = asset.comparison_mode === 'single'
        ? {
            primary_cropped_url: url2,
            secondary_cropped_url: url2,
          }
        : {
            primary_cropped_url: url1,
            secondary_cropped_url: url2,
          };

      await updateAsset(assetId, updateData);

      toast.success('Images updated successfully');

      // Close editor and clear state
      setIsEditMode(false);
      setEditorUrls(null);

      // Refetch the asset data
      refetch();
    } catch (error) {
      console.error('Error saving images:', error);
      toast.error('Failed to save images');
    }
  };

  const handleCancelEditor = () => {
    setIsEditMode(false);
    setEditorUrls(null);
  };

  const handleEditText = () => {
    setEditedHeader(asset?.header || '');
    setEditedSubheader(asset?.subheader || '');
    setIsEditingText(true);
  };

  const handleSaveText = async () => {
    if (!assetId) return;

    setIsSaving(true);
    try {
      await updateAsset(assetId, {
        header: editedHeader || null,
        subheader: editedSubheader || null,
      });

      toast.success('Header and subheader updated successfully');
      setIsEditingText(false);
      refetch();
    } catch (error) {
      console.error('Error updating asset:', error);
      toast.error('Failed to update asset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelTextEdit = () => {
    setIsEditingText(false);
    setEditedHeader('');
    setEditedSubheader('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading asset...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error || 'Asset not found'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="py-8">
          {/* Breadcrumbs */}
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
            <Link
              to="/collections"
              className="hover:text-gray-900 transition-colors"
            >
              Collections
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link
              to={`/collections/${collectionId}`}
              className="hover:text-gray-900 transition-colors"
            >
              {asset.collection?.name || 'Collection'}
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-900 font-medium">
              {asset.title || asset.slug || 'Asset'}
            </span>
          </nav>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {asset.slug || 'Asset Detail'}
              </h1>
              <Badge variant={asset.type === 'diff' ? 'default' : 'secondary'}>
                {asset.type}
              </Badge>
              {asset.comparison_mode && asset.comparison_mode !== 'single' && (
                <Badge variant="outline">
                  {asset.comparison_mode.replace('_', ' ')}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {asset.version && (
                <span>Version: {asset.version}</span>
              )}
              {asset.secondary_version && (
                <span>Secondary: {asset.secondary_version}</span>
              )}
              {asset.crop_aspect_ratio && (
                <span>Aspect: {asset.crop_aspect_ratio}</span>
              )}
            </div>
          </div>

          {/* Header/Subheader Edit Card */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Asset Text</CardTitle>
              {!isEditingText && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditText}
                  className="flex items-center gap-2"
                >
                  <Edit3 className="h-3 w-3" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditingText ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="header">Header</Label>
                    <Input
                      id="header"
                      value={editedHeader}
                      onChange={(e) => setEditedHeader(e.target.value)}
                      placeholder="Enter header text"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subheader">Subheader</Label>
                    <Input
                      id="subheader"
                      value={editedSubheader}
                      onChange={(e) => setEditedSubheader(e.target.value)}
                      placeholder="Enter subheader text"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleSaveText}
                      disabled={isSaving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelTextEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Header</span>
                    <p className="text-gray-900 mt-1">{asset.header || <span className="text-gray-400 italic">No header set</span>}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Subheader</span>
                    <p className="text-gray-900 mt-1">{asset.subheader || <span className="text-gray-400 italic">No subheader set</span>}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Asset Viewer */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px] relative">
            {/* Edit Image(s) Button Overlay */}
            <Button
              variant="default"
              onClick={handleEdit}
              className="absolute top-4 right-4 z-10 flex items-center gap-2 shadow-lg"
            >
              <Image className="h-4 w-4" />
              Edit Image(s)
            </Button>

            <EmbeddedAssetViewer
              assetId={assetId!}
              hideEditButtons={true}
              hideHeader={true}
            />
          </div>

          {/* Side-by-Side Image Editor Dialog */}
          <Dialog open={isEditMode} onOpenChange={setIsEditMode}>
            <DialogContent className="max-w-[95vw] h-[90vh] p-0">
              <div className="h-full flex flex-col">
                {editorUrls && (
                  <SideBySideEditor
                    url1={editorUrls.url1}
                    url2={editorUrls.url2}
                    onSave={handleSaveImages}
                    onCancel={handleCancelEditor}
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailPage;